/**
 * Route an inline English/German dictionary through the translation table
 * without touching its call sites (COL-479).
 *
 * The tenant dashboard reads its copy from plain objects —
 * `const p = getPaymentsLabels(language); … {p.monthlyRent}` — repeated across
 * hundreds of JSX sites. Rewriting every one of those into a `t()` call would
 * be a huge, risky diff for no behavioural gain. Instead the dictionary itself
 * is translated once, in place: same object shape, same keys, values resolved
 * through `t()` first.
 *
 * The caller passes the dictionary for the *current base language*, so the
 * fallback is always copy that already exists:
 *
 *   translateDictionary("tenant.payments", getPaymentsLabels(base), t)
 *
 *   - German tenant: German row from the table, else the German literal.
 *   - English tenant: English row, else the English literal.
 *   - French tenant: base is English, so the French row wins when seeded and
 *     the English literal shows until it is.
 *
 * With an empty or unreachable translations table every language renders
 * exactly what it rendered before this change.
 *
 * Pass `tDict`, not `t`: these literals are already written in the reader's
 * own language, so they must outrank a row in a different one. With plain
 * `t()` a German tenant whose `de` column is empty would render the English
 * row instead of the German literal sitting right here.
 *
 * Values that are not strings are returned untouched: the dictionaries carry
 * a dozen template *functions* (`usePayNowToSettle: (month) => …`), and a
 * function body cannot be translated by a key lookup. Those are converted
 * surface by surface as their call sites are swept; `untranslatableKeys`
 * reports them so the seeding script can list what it skipped.
 */
export type TranslateFn = (key: string, fallback?: string) => string;

function keyFor(namespace: string, key: string): string {
  return `${namespace}.${key}`;
}

/**
 * A translated copy of `dict`. Strings and arrays of strings are looked up by
 * `<namespace>.<key>` (array items by `<namespace>.<key>.<index>`); anything
 * else is passed through.
 */
export function translateDictionary<T extends Record<string, unknown>>(
  namespace: string,
  dict: T,
  t: TranslateFn
): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(dict)) {
    if (typeof value === "string") {
      out[key] = t(keyFor(namespace, key), value);
    } else if (
      Array.isArray(value) &&
      value.every((item) => typeof item === "string")
    ) {
      out[key] = (value as string[]).map((item, index) =>
        t(`${keyFor(namespace, key)}.${index}`, item)
      );
    } else {
      out[key] = value;
    }
  }
  return out as T;
}

/** Keys whose value is neither a string nor a string array, in source order. */
export function untranslatableKeys(dict: Record<string, unknown>): string[] {
  return Object.entries(dict)
    .filter(
      ([, value]) =>
        typeof value !== "string" &&
        !(
          Array.isArray(value) &&
          value.every((item) => typeof item === "string")
        )
    )
    .map(([key]) => key);
}

/**
 * Every translatable key of a dictionary, paired with its literal value.
 * Used by the seeding script; the array form is flattened to indexed keys so
 * a seeded row exists for each item.
 */
export function translatableEntries(
  namespace: string,
  dict: Record<string, unknown>
): Array<{ key: string; value: string }> {
  const entries: Array<{ key: string; value: string }> = [];
  for (const [key, value] of Object.entries(dict)) {
    if (typeof value === "string") {
      entries.push({ key: keyFor(namespace, key), value });
    } else if (
      Array.isArray(value) &&
      value.every((item) => typeof item === "string")
    ) {
      (value as string[]).forEach((item, index) =>
        entries.push({ key: `${keyFor(namespace, key)}.${index}`, value: item })
      );
    }
  }
  return entries;
}
